import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, TrendingUp, FolderKanban, Briefcase, Wrench, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import DashboardLayout from "@/components/DashboardLayout";
import { StatusBadge, Empty } from "@/components/Shared";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/student/dashboard").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return <DashboardLayout title="Dashboard"><p className="text-sm text-muted-foreground">Loading your journey...</p></DashboardLayout>;

    const stats = [
        { label: "Projects", value: `${data.progress.projects} / 5`, icon: FolderKanban },
        { label: "Internships", value: `${data.progress.internships_done} / 1`, icon: Briefcase },
        { label: "Skills", value: data.progress.skills, icon: Wrench },
        { label: "Certificates", value: data.progress.certificates, icon: Award },
    ];

    return (
        <DashboardLayout title={`Hello, ${user?.name?.split(" ")[0]}`}>
            <div className="space-y-8" data-testid="student-dashboard">
                <div className="grid gap-6 md:grid-cols-3">
                    <div className="card-soft p-6" data-testid="profile-completion-card">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Profile Completion</p>
                        <p className="mt-3 font-display text-4xl font-bold text-primary">{data.profile_completion}%</p>
                        <Progress value={data.profile_completion} className="mt-4 h-2" />
                        <Link to="/profile" data-testid="complete-profile-link" className="mt-4 inline-flex items-center text-xs font-semibold text-primary">Complete your profile <ArrowRight className="ml-1 h-3 w-3" /></Link>
                    </div>
                    <div className="card-soft p-6" data-testid="current-goal-card">
                        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Target className="h-3.5 w-3.5" /> Current Goal</p>
                        <p className="mt-3 font-display text-xl font-semibold leading-snug">{data.current_goal}</p>
                    </div>
                    <div className="card-soft border-primary/30 bg-primary/5 p-6" data-testid="next-step-card">
                        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary"><TrendingUp className="h-3.5 w-3.5" /> Recommended Next Step</p>
                        <p className="mt-3 font-display text-base font-semibold leading-snug">{data.next_step.title}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{data.next_step.detail}</p>
                        <Link to={data.next_step.link} data-testid="next-step-link"><Button size="sm" className="mt-4 rounded-full">Take action</Button></Link>
                    </div>
                </div>

                <div>
                    <h2 className="font-display text-xl font-semibold">Recommended Opportunities</h2>
                    {data.recommended.length === 0 ? (
                        <div className="mt-4"><Empty icon={Briefcase} title="No new recommendations right now" hint="You've applied to everything that matches you. Check back soon!" /></div>
                    ) : (
                        <div className="mt-4 grid gap-5 md:grid-cols-2" data-testid="recommended-opportunities">
                            {data.recommended.map((r) => (
                                <div key={r.id} className="card-soft flex flex-col p-6" data-testid={`recommended-${r.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="font-display text-base font-semibold">{r.title}</p>
                                            <p className="mt-1 text-xs text-muted-foreground">{r.duration_months} Months · ${r.fee_usd} · {r.mode}</p>
                                        </div>
                                        <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary" data-testid={`match-${r.id}`}>{r.match}% match</span>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {r.skills?.slice(0, 4).map((s) => <span key={s} className="rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground">{s}</span>)}
                                    </div>
                                    <Link to={`/internships/${r.id}`} className="mt-5" data-testid={`apply-recommended-${r.id}`}>
                                        <Button size="sm" className="rounded-full">Apply Now</Button>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="grid gap-8 lg:grid-cols-5">
                    <div className="lg:col-span-2">
                        <h2 className="font-display text-xl font-semibold">Your Progress</h2>
                        <div className="mt-4 grid grid-cols-2 gap-4" data-testid="progress-stats">
                            {stats.map((s) => (
                                <div key={s.label} className="card-soft p-5">
                                    <s.icon className="h-5 w-5 text-primary" />
                                    <p className="mt-3 font-display text-2xl font-bold">{s.value}</p>
                                    <p className="text-xs text-muted-foreground">{s.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-3">
                        <div className="flex items-center justify-between">
                            <h2 className="font-display text-xl font-semibold">My Applications</h2>
                            <Link to="/applications" data-testid="view-all-applications-link" className="text-xs font-semibold text-primary">View all</Link>
                        </div>
                        <div className="card-soft mt-4 divide-y divide-border/40" data-testid="dashboard-applications">
                            {data.applications.length === 0 && <p className="p-6 text-sm text-muted-foreground">No applications yet. Your journey starts with one click.</p>}
                            {data.applications.map((a) => (
                                <div key={a.id} className="flex items-center justify-between gap-4 p-5">
                                    <div>
                                        <p className="font-display text-sm font-semibold">{a.internship_title}</p>
                                        <p className="text-xs text-muted-foreground">{a.duration_months} Months · ${a.fee_usd}</p>
                                    </div>
                                    <StatusBadge status={a.status} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}
